import { z } from 'zod';

/**
 * Generic address schema that can be used across different modules
 */
export const AddressSchema = z.object({
  street: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  country: z.string().optional(),
  postalCode: z.string().optional(),
  formatted: z.string().optional(),
});

/**
 * Type definition for the address schema
 */
export type Address = z.infer<typeof AddressSchema>;

/**
 * Format an address object into a human-readable string
 */
export function formatAddress(address: Address): string {
  const parts = [
    address.street,
    [address.city, address.state, address.postalCode].filter(Boolean).join(', '),
    address.country
  ].filter(Boolean);

  return parts.join('\n');
}

/**
 * Parse a formatted address string into an Address object
 */
export function parseAddress(formattedAddress: string): Partial<Address> {
  // Simple parsing logic - can be enhanced based on needs
  const lines = formattedAddress.split('\n').map(line => line.trim());
  
  if (lines.length === 0) return {};
  
  // Assume first line is street
  const address: Partial<Address> = {
    street: lines[0]
  };
  
  // If we have more lines
  if (lines.length > 1) {
    // Last line is typically country
    if (lines.length > 2) {
      address.country = lines[lines.length - 1];
    }
    
    // Middle line typically contains city, state, postal code
    const cityStateLineIndex = lines.length > 2 ? lines.length - 2 : 1;
    const cityStateLine = lines[cityStateLineIndex];
    
    if (cityStateLine) {
      const parts = cityStateLine.split(',').map(part => part.trim());
      
      if (parts.length > 0) {
        address.city = parts[0];
        
        if (parts[1]) {
          // Try to separate state and postal code if they exist
          const statePostal = parts[1].split(' ').map(part => part.trim());
          if (statePostal.length > 0) {
            address.state = statePostal[0];
            if (statePostal.length > 1) {
              address.postalCode = statePostal.slice(1).join(' ');
            }
          }
        }
      }
    }
  }
  
  return address;
} 